import React, { useRef, useState } from "react";
import { MdCloudUpload } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import { InputField, Textarea } from "components/fields";

const MAX_LOGO_BYTES = 5 * 1024 * 1024;

const ProfileSection = ({ displayName, tagline, logoUrl, onFieldChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notify.error("Please upload an image file");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      notify.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const upload = new FormData();
      upload.append("file", file);
      upload.append("type", "logo");

      const response = await AtriumApi.post("/connect/upload", upload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        onFieldChange("logoUrl", response.data.url);
        notify.success("Logo uploaded");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not upload logo"));
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => onFieldChange(e.target.name, e.target.value);

  return (
    <Card padding="md" className="gap-5">
      <CardHeader title="Profile Settings" />

      <div>
        <p className="mb-1.5 text-sm font-medium text-fg">Logo</p>
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-line bg-surface-raised text-fg-muted">
            {logoUrl ? (
              <img src={logoUrl} alt="Current logo" className="h-full w-full object-cover" />
            ) : (
              <MdCloudUpload aria-hidden="true" className="h-8 w-8" />
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <Button
              variant="secondary"
              leftIcon={<MdCloudUpload aria-hidden="true" />}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Uploading…" : "Upload Logo"}
            </Button>
            <p className="mt-1 text-xs text-fg-muted">JPG, PNG or WebP. Max 5MB.</p>
          </div>
        </div>
      </div>

      <InputField
        label="Display Name"
        name="displayName"
        value={displayName}
        onChange={handleChange}
        placeholder="Your Church Name"
      />

      <Textarea
        label="Tagline"
        name="tagline"
        value={tagline}
        onChange={handleChange}
        placeholder="Welcome to our community"
        rows={2}
      />
    </Card>
  );
};

export default ProfileSection;
